import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

export default function User() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [races, setRaces] = useState([]);
	const [userRaces, setUserRaces] = useState([]);
	const [flights, setFlights] = useState([]);
	const [rentals, setRentals] = useState([]);
	const [hotels, setHotels] = useState([]);

	useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) {
			navigate('/');
			return;
		}

		const userId = id;

		async function load() {
			try {
				// Fetch user (backend uses POST for this endpoint)
				const uRes = await fetch(`/api/users/${userId}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
				});
				if (!uRes.ok) {
					navigate('/');
					return;
				}
				const uData = await uRes.json();
				setUser(uData.user || null);

				// Fetch races list
				const racesRes = await fetch('/api/races', { headers: { Authorization: `Bearer ${token}` } });
				const racesData = await racesRes.json();
				setRaces(racesData || []);

				// For each race, fetch attendees and hotels to determine which races this user attends and collect hotels
				const userRacesLocal = [];
				const userHotelsLocal = [];

				await Promise.all((racesData || []).map(async (r) => {
					try {
						const attRes = await fetch(`/api/races/${r.id}/attendees`, { headers: { Authorization: `Bearer ${token}` } });
						const att = await attRes.json();
						if ((att || []).some(a => String(a.id) === String(userId))) {
							userRacesLocal.push(r);
						}
					} catch (e) {
						// ignore per-race attendee failures
					}
					try {
						const hRes = await fetch(`/api/races/${r.id}/hotels`, { headers: { Authorization: `Bearer ${token}` } });
						const h = await hRes.json();
						(h || []).filter(hh => String(hh.user_id) === String(userId)).forEach(x => userHotelsLocal.push({ ...x, race: r }));
					} catch (e) {
						// ignore
					}
				}));

				setUserRaces(userRacesLocal);
				setHotels(userHotelsLocal);

				// Flights (all, filter client-side)
				try {
					const fRes = await fetch(`/api/flights`, { headers: { Authorization: `Bearer ${token}` } });
					const f = await fRes.json();
					setFlights((f || []).filter(ff => String(ff.user_id) === String(userId)));
				} catch (e) {
					setFlights([]);
				}

				// Car rentals
				try {
					const rRes = await fetch(`/api/car_rentals`, { headers: { Authorization: `Bearer ${token}` } });
					const rr = await rRes.json();
					setRentals((rr || []).filter(x => String(x.user_id) === String(userId)));
				} catch (e) {
					setRentals([]);
				}

			} catch (e) {
				console.error(e);
			} finally {
				setLoading(false);
			}
		}

		load();
	}, [id, navigate]);

	if (loading) return <div className="p-6">Loading...</div>;
	if (!user) return <div className="p-6">User not found</div>;

	const nextFlight = (flights || []).slice().sort((a,b) => new Date(a.departure_time) - new Date(b.departure_time))[0];
	const nextRental = (rentals || []).slice().sort((a,b) => new Date(a.rental_date) - new Date(b.rental_date))[0];

	return (
			<div className="p-6 max-w-4xl mx-auto">
				<div className="bg-white rounded-lg shadow-lg overflow-hidden">
					<div className="md:flex">
						<aside className="md:w-1/3 bg-gradient-to-b from-white to-gray-50 p-6 text-center">
							{user.profile_pic ? (
								<img
									src={`/assets/profile-pic/${user.profile_pic}`}
									alt={user.name}
									className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-md mx-auto"
									onError={e => { e.currentTarget.onerror = null; e.currentTarget.style.display = 'none'; }}
								/>
							) : (
								<div className="w-28 h-28 rounded-full bg-gray-300 flex items-center justify-center text-4xl font-bold mx-auto">{user.name ? user.name[0].toUpperCase() : '?'}</div>
							)}

							<h2 className="mt-4 text-xl font-semibold">{user.name}</h2>
							<div className="text-sm text-gray-500">{user.email}</div>

							<div className="mt-6 text-sm text-gray-600">
								<div><span className="font-medium">Admin:</span> {user.admin ? 'Yes' : 'No'}</div>
								<div className="mt-2"><span className="font-medium">Flights:</span> {flights.length}</div>
								<div><span className="font-medium">Rentals:</span> {rentals.length}</div>
							</div>
						</aside>

						<main className="md:flex-1 p-6">
							<h3 className="text-lg font-semibold mb-4">Overview</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="p-4 border rounded">
									<div className="text-sm text-gray-500">Next flight</div>
									{nextFlight ? (
										<div className="mt-2">
											<div className="font-medium">{nextFlight.flight_number}</div>
											<div className="text-sm text-gray-600">{nextFlight.departure} → {nextFlight.arrival}</div>
											<div className="text-xs text-gray-400 mt-1">{nextFlight.departure_time ? new Date(nextFlight.departure_time).toLocaleString() : ''}</div>
										</div>
									) : (
										<div className="mt-2 text-sm text-gray-500">No upcoming flights</div>
									)}
								</div>

								<div className="p-4 border rounded">
									<div className="text-sm text-gray-500">Next rental</div>
									{nextRental ? (
										<div className="mt-2">
											<div className="font-medium">{nextRental.car_model}</div>
											<div className="text-sm text-gray-600">{nextRental.store}</div>
											<div className="text-xs text-gray-400 mt-1">{nextRental.rental_date ? new Date(nextRental.rental_date).toLocaleString() : ''}</div>
										</div>
									) : (
										<div className="mt-2 text-sm text-gray-500">No upcoming rentals</div>
									)}
								</div>
							</div>

							<section className="mt-6">
								<h4 className="text-md font-semibold mb-2">Races attending</h4>
								{userRaces.length === 0 ? (
									<div className="text-sm text-gray-500">No races</div>
								) : (
									<div className="space-y-2">
										{userRaces.map(r => (
											<div key={r.id} className="p-3 border rounded flex items-center justify-between">
												<div>
													<div className="font-medium">{r.name}</div>
													<div className="text-sm text-gray-600">{r.location}</div>
												</div>
												<div className="text-xs text-gray-400">{r.start_date ? new Date(r.start_date).toLocaleDateString() : ''}</div>
											</div>
										))}
									</div>
								)}
							</section>

							<section className="mt-6">
								<h4 className="text-md font-semibold mb-2">Flights</h4>
								{flights.length === 0 ? (
									<div className="text-sm text-gray-500">No flights</div>
								) : (
									<div className="space-y-2">
										{flights.slice(0,5).map(f => (
											<div key={f.id} className="p-3 border rounded flex items-center justify-between">
												<div>
													<div className="font-medium">{f.flight_number}</div>
													<div className="text-sm text-gray-600">{f.departure} → {f.arrival}</div>
												</div>
												<div className="text-xs text-gray-400">{f.departure_time ? new Date(f.departure_time).toLocaleString() : ''}</div>
											</div>
										))}
									</div>
								)}
							</section>

							<section className="mt-6">
								<h4 className="text-md font-semibold mb-2">Rented cars</h4>
								{rentals.length === 0 ? (
									<div className="text-sm text-gray-500">No rentals</div>
								) : (
									<div className="space-y-2">
										{rentals.slice(0,5).map(r => (
											<div key={r.id} className="p-3 border rounded flex items-center justify-between">
												<div>
													<div className="font-medium">{r.car_model}</div>
													<div className="text-sm text-gray-600">{r.store}</div>
												</div>
												<div className="text-xs text-gray-400">{r.rental_date ? new Date(r.rental_date).toLocaleString() : ''}</div>
											</div>
										))}
									</div>
								)}
							</section>

							<section className="mt-6">
								<h4 className="text-md font-semibold mb-2">Hotels</h4>
								{hotels.length === 0 ? (
									<div className="text-sm text-gray-500">No hotels</div>
								) : (
									<div className="space-y-2">
										{hotels.map(h => (
											<div key={h.id} className="p-3 border rounded flex items-center justify-between">
												<div>
													<div className="font-medium">{h.hotel_name}</div>
													<div className="text-sm text-gray-600">{h.race ? h.race.name : ''}</div>
												</div>
												<div className="text-xs text-gray-400">{h.check_in ? new Date(h.check_in).toLocaleDateString() : ''} → {h.check_out ? new Date(h.check_out).toLocaleDateString() : ''}</div>
											</div>
										))}
									</div>
								)}
							</section>
						</main>
					</div>
				</div>
			</div>
	);
}

